import { QUOTA_CATEGORIES, QUOTA_LABELS, formatEuros, type PlatformPlan, type PlatformTenant, type QuotaKey } from '@clim/domain';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Empty, ErrorBox, Field, Input, Modal, Select } from '../components/ui';

const API_URL = import.meta.env.VITE_API_URL ?? '';
const KEY = 'clim_platform_token';

/**
 * Espace plateforme (§19) — super-administrateur, distinct des entreprises : connexion séparée (jeton de 30 min
 * conservé le temps de l'onglet), entreprises (usage / quotas, création, suspension, abonnement, support),
 * abonnements, journal. Aucune donnée métier des entreprises n'est visible ici.
 */
export function PlatformPage() {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(KEY);
    } catch {
      return null;
    }
  });
  const save = (t: string | null) => {
    setToken(t);
    try {
      if (t) sessionStorage.setItem(KEY, t);
      else sessionStorage.removeItem(KEY);
    } catch {
      /* private mode: memory only */
    }
  };
  return (
    <div className="platform">
      <header className="topbar">
        <span className="brand">Krotime — Plateforme</span>
        {token && (
          <button className="btn ghost small" onClick={() => save(null)}>
            Déconnexion
          </button>
        )}
      </header>
      <main className="content">{token ? <Console token={token} onExpired={() => save(null)} /> : <Login onToken={save} />}</main>
    </div>
  );
}

class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

function usePlatformApi(token: string, onExpired: () => void) {
  return useCallback(
    async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
      const res = await fetch(`${API_URL}/v1/platform${path}`, {
        method,
        headers: { authorization: `Bearer ${token}`, ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => null);
      if (res.status === 401) onExpired();
      if (!res.ok) throw new HttpError(res.status, data?.error?.message ?? `Erreur ${res.status}`);
      return data as T;
    },
    [token, onExpired],
  );
}

function Login({ onToken }: { onToken(t: string): void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const res = await fetch(`${API_URL}/v1/platform/auth/login`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, password }) });
    const data = await res.json().catch(() => null);
    if (!res.ok) return setError(data?.error?.message ?? 'Connexion impossible');
    onToken(data.accessToken);
  };
  return (
    <form className="card form login-card" onSubmit={(e) => void submit(e)}>
      <h1>Espace plateforme</h1>
      <p className="muted">Réservé aux super-administrateurs de la plateforme (distinct des comptes d’entreprise).</p>
      <Field label="E-mail">
        <Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field label="Mot de passe">
        <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      <ErrorBox message={error} />
      <button className="btn primary" type="submit">
        Se connecter
      </button>
    </form>
  );
}

function Console({ token, onExpired }: { token: string; onExpired(): void }) {
  const call = usePlatformApi(token, onExpired);
  const [tab, setTab] = useState<'tenants' | 'plans' | 'journal'>('tenants');
  const [tenants, setTenants] = useState<PlatformTenant[] | null>(null);
  const [plans, setPlans] = useState<PlatformPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    Promise.all([call<PlatformTenant[]>('GET', '/tenants'), call<PlatformPlan[]>('GET', '/plans')]).then(
      ([t, p]) => (setTenants(t), setPlans(p), setError(null)),
      (e) => setError((e as Error).message),
    );
  }, [call]);
  useEffect(load, [load]);
  return (
    <div>
      <div className="chips" role="tablist">
        {(
          [
            ['tenants', `Entreprises${tenants ? ` (${tenants.length})` : ''}`],
            ['plans', 'Abonnements'],
            ['journal', 'Journal'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={`chip${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>
      <ErrorBox message={error} />
      {tab === 'tenants' && tenants && <Tenants tenants={tenants} plans={plans} call={call} reload={load} />}
      {tab === 'plans' && <Plans plans={plans} call={call} reload={load} />}
      {tab === 'journal' && <Journal call={call} />}
    </div>
  );
}

type Call = <T>(method: string, path: string, body?: unknown) => Promise<T>;
const limitText = (v: number | null | undefined) => (v === null || v === undefined ? '∞' : String(v));

function Tenants({ tenants, plans, call, reload }: { tenants: PlatformTenant[]; plans: PlatformPlan[]; call: Call; reload(): void }) {
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<PlatformTenant | null>(null);
  return (
    <div>
      <div className="filters">
        <button className="btn primary" onClick={() => setCreating(true)}>
          + Nouvelle entreprise
        </button>
      </div>
      <table className="table" aria-label="Entreprises">
        <thead>
          <tr>
            <th>Entreprise</th>
            <th>Abonnement</th>
            {QUOTA_CATEGORIES.map((c) => (
              <th key={c}>{QUOTA_LABELS[c]}</th>
            ))}
            <th>Dossiers</th>
            <th>Fichiers</th>
            <th>Statut</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr key={t.id} className="clickable" onClick={() => setOpen(t)}>
              <td>
                <strong>{t.name}</strong>
                <div className="muted">{t.slug}</div>
              </td>
              <td>{t.plan?.name ?? '—'}</td>
              {QUOTA_CATEGORIES.map((c) => {
                const lim = t.limits[c];
                const over = typeof lim === 'number' && t.usage[c] > lim;
                return (
                  <td key={c} className={over ? 'warn' : ''}>
                    {t.usage[c]} / {limitText(lim)}
                  </td>
                );
              })}
              <td>{t.usage.dossiers}</td>
              <td className="nowrap">{t.usage.storageMb} Mo</td>
              <td>
                <span className={`badge tone-${t.status === 'active' ? 'success' : 'danger'}`}>{t.status === 'active' ? 'Active' : 'Suspendue'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {creating && <CreateTenant plans={plans} call={call} onClose={() => (setCreating(false), reload())} />}
      {open && <TenantDialog tenant={open} plans={plans} call={call} onClose={() => (setOpen(null), reload())} />}
    </div>
  );
}

function OneTimePassword({ email, password }: { email: string; password: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="card legal-alert" role="status">
      <p>
        Mot de passe provisoire de <strong>{email}</strong> (affiché une seule fois) :
      </p>
      <p>
        <code className="link-box" aria-label="Mot de passe provisoire">
          {password}
        </code>{' '}
        <button className="btn small" onClick={() => void navigator.clipboard.writeText(password).then(() => setCopied(true))}>
          {copied ? 'Copié ✓' : 'Copier'}
        </button>
      </p>
      <p className="muted">Transmettez-le à la personne par un canal sûr (de vive voix, par téléphone). Elle devra le changer à sa première connexion.</p>
    </div>
  );
}

function CreateTenant({ plans, call, onClose }: { plans: PlatformPlan[]; call: Call; onClose(): void }) {
  const [f, setF] = useState({ name: '', slug: '', planCode: plans.find((p) => p.isActive)?.code ?? '', adminEmail: '', adminFirstName: '', adminLastName: '' });
  const [terr, setTerr] = useState<string[]>([]);
  const [done, setDone] = useState<{ slug: string; adminEmail: string; temporaryPassword: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    setError(null);
    try {
      setDone(await call('POST', '/tenants', { ...f, territoryCodes: terr }));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Modal title="Nouvelle entreprise" onClose={onClose}>
      {done ? (
        <div className="form">
          <p>
            Entreprise créée. Adresse de connexion : identifiant <strong>{done.slug}</strong>.
          </p>
          <OneTimePassword email={done.adminEmail} password={done.temporaryPassword} />
          <p className="muted">
            Référentiels de lancement et textes juridiques repris comme modèles : l’entreprise doit compléter son identité légale et faire valider ses textes
            (signature refusée en production tant qu’ils ne sont pas validés).
          </p>
          <button className="btn" onClick={onClose}>
            Fermer
          </button>
        </div>
      ) : (
        <div className="form">
          <Field label="Nom de l’entreprise">
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: f.slug || e.target.value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') })} />
          </Field>
          <Field label="Identifiant (connexion)" hint="minuscules, chiffres et tirets">
            <Input value={f.slug} onChange={set('slug')} />
          </Field>
          <Field label="Abonnement">
            <Select value={f.planCode} onChange={set('planCode')}>
              {plans
                .filter((p) => p.isActive)
                .map((p) => (
                  <option key={p.id} value={p.code}>
                    {p.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Territoires de départ (modèles)">
            <div className="chips">
              {[
                ['GP', 'Guadeloupe'],
                ['MQ', 'Martinique'],
              ].map(([c, l]) => (
                <label key={c} className="check">
                  <input type="checkbox" checked={terr.includes(c!)} onChange={() => setTerr(terr.includes(c!) ? terr.filter((x) => x !== c) : [...terr, c!])} /> {l}
                </label>
              ))}
            </div>
          </Field>
          <div className="row-inline">
            <Field label="Prénom de l’administrateur">
              <Input value={f.adminFirstName} onChange={set('adminFirstName')} />
            </Field>
            <Field label="Nom">
              <Input value={f.adminLastName} onChange={set('adminLastName')} />
            </Field>
          </div>
          <Field label="E-mail de l’administrateur">
            <Input type="email" value={f.adminEmail} onChange={set('adminEmail')} />
          </Field>
          <ErrorBox message={error} />
          <button className="btn primary" onClick={() => void create()} disabled={!f.name || !f.slug || !f.adminEmail}>
            Créer l’entreprise
          </button>
        </div>
      )}
    </Modal>
  );
}

function TenantDialog({ tenant: t, plans, call, onClose }: { tenant: PlatformTenant; plans: PlatformPlan[]; call: Call; onClose(): void }) {
  const [planId, setPlanId] = useState(t.plan?.id ?? '');
  const [reason, setReason] = useState('');
  const [reset, setReset] = useState<{ email: string; temporaryPassword: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const patch = async (body: Record<string, unknown>) => {
    setError(null);
    try {
      await call('PATCH', `/tenants/${t.id}`, body);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Modal title={t.name} onClose={onClose}>
      <dl className="kv">
        <dt>Identifiant</dt>
        <dd>{t.slug}</dd>
        <dt>Créée le</dt>
        <dd>{new Date(t.createdAt).toLocaleDateString('fr-FR')}</dd>
        <dt>Utilisateurs actifs</dt>
        <dd>{t.usage.users}</dd>
        <dt>Administrateurs</dt>
        <dd>
          {t.admins.map((a) => (
            <div key={a.email}>
              {a.name} · {a.email}{' '}
              <button className="btn ghost small" onClick={() => void call<{ email: string; temporaryPassword: string }>('POST', `/tenants/${t.id}/admin-reset`, { email: a.email }).then(setReset, (e) => setError((e as Error).message))}>
                Nouveau mot de passe
              </button>
            </div>
          ))}
        </dd>
      </dl>
      {reset && <OneTimePassword email={reset.email} password={reset.temporaryPassword} />}
      <div className="form">
        <div className="row-inline">
          <Field label="Abonnement">
            <Select value={planId} onChange={(e) => setPlanId(e.target.value)}>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.isActive ? '' : ' (retiré)'}
                </option>
              ))}
            </Select>
          </Field>
          <button className="btn" disabled={planId === t.plan?.id} onClick={() => void patch({ planId })}>
            Changer d’abonnement
          </button>
        </div>
        {t.status === 'active' ? (
          <div className="row-inline">
            <Field label="Motif de suspension">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <button className="btn danger-outline" disabled={!reason.trim()} onClick={() => void patch({ status: 'suspended', reason })}>
              Suspendre
            </button>
          </div>
        ) : (
          <button className="btn primary" onClick={() => void patch({ status: 'active' })}>
            Réactiver l’entreprise
          </button>
        )}
        <p className="muted">Une suspension coupe immédiatement l’accès de tous les utilisateurs de l’entreprise ; aucune donnée n’est supprimée.</p>
      </div>
      <ErrorBox message={error} />
    </Modal>
  );
}

function Plans({ plans, call, reload }: { plans: PlatformPlan[]; call: Call; reload(): void }) {
  const [edit, setEdit] = useState<PlatformPlan | null>(null);
  const [draft, setDraft] = useState<Record<QuotaKey, string> & { name: string; price: string }>({ supervisor: '', commercial: '', technician: '', name: '', price: '' });
  const [warn, setWarn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const start = (p: PlatformPlan) => {
    setEdit(p);
    setDraft({ name: p.name, price: p.monthlyPriceCents === null ? '' : String(p.monthlyPriceCents / 100), supervisor: p.limits.supervisor?.toString() ?? '', commercial: p.limits.commercial?.toString() ?? '', technician: p.limits.technician?.toString() ?? '' });
  };
  const save = async () => {
    if (!edit) return;
    const lim = (v: string) => (v.trim() === '' ? null : Number(v));
    try {
      const r = await call<{ overQuota: { tenant: string; category: QuotaKey; usage: number; limit: number }[] }>('PATCH', `/plans/${edit.id}`, {
        name: draft.name,
        monthlyPriceCents: draft.price.trim() === '' ? null : Math.round(Number(draft.price.replace(',', '.')) * 100),
        limits: { supervisor: lim(draft.supervisor), commercial: lim(draft.commercial), technician: lim(draft.technician) },
      });
      setWarn(r.overQuota.length ? `Au-dessus de la nouvelle limite (les utilisateurs existants sont conservés, seuls les ajouts sont bloqués) : ${r.overQuota.map((o) => `${o.tenant} — ${QUOTA_LABELS[o.category]} ${o.usage}/${o.limit}`).join(', ')}` : null);
      setEdit(null);
      reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <div>
      {warn && <p className="warn">{warn}</p>}
      <table className="table" aria-label="Abonnements">
        <thead>
          <tr>
            <th>Abonnement</th>
            <th>Prix / mois</th>
            {QUOTA_CATEGORIES.map((c) => (
              <th key={c}>{QUOTA_LABELS[c]}</th>
            ))}
            <th>Entreprises</th>
          </tr>
        </thead>
        <tbody>
          {plans.map((p) => (
            <tr key={p.id} className={`clickable${p.isActive ? '' : ' disabled'}`} onClick={() => start(p)}>
              <td>
                <strong>{p.name}</strong>
                <div className="muted">{p.code}</div>
              </td>
              <td>{p.monthlyPriceCents === null ? '—' : formatEuros(p.monthlyPriceCents)}</td>
              {QUOTA_CATEGORIES.map((c) => (
                <td key={c}>{limitText(p.limits[c])}</td>
              ))}
              <td>{p.tenantCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted">Limite vide = illimité. Les quotas sont appliqués par le serveur à chaque création ou activation d’utilisateur.</p>
      {edit && (
        <Modal title={`Abonnement — ${edit.name}`} onClose={() => setEdit(null)}>
          <div className="form">
            <Field label="Nom">
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label="Prix mensuel indicatif (€ HT)">
              <Input inputMode="decimal" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} />
            </Field>
            {QUOTA_CATEGORIES.map((c) => (
              <Field key={c} label={`${QUOTA_LABELS[c]} (vide = illimité)`}>
                <Input inputMode="numeric" value={draft[c]} onChange={(e) => setDraft({ ...draft, [c]: e.target.value })} />
              </Field>
            ))}
            <ErrorBox message={error} />
            <button className="btn primary" onClick={() => void save()}>
              Enregistrer
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Journal({ call }: { call: Call }) {
  const [rows, setRows] = useState<{ id: string; action: string; at: string; adminName: string | null; tenantName: string | null; ip: string | null }[] | null>(null);
  useEffect(() => {
    void call<typeof rows>('GET', '/journal').then(setRows);
  }, [call]);
  if (!rows) return null;
  if (!rows.length) return <Empty>Journal vide.</Empty>;
  return (
    <table className="table compact" aria-label="Journal de la plateforme">
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td className="nowrap">{new Date(r.at).toLocaleString('fr-FR')}</td>
            <td>{r.action}</td>
            <td>{r.adminName ?? '—'}</td>
            <td>{r.tenantName ?? ''}</td>
            <td className="muted">{r.ip ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
